import axios from 'axios';
import { APIEndpoints } from '../config/APIEndpoints';

export class PythonBackendService {
  private baseUrl: string;

  constructor(baseUrl: string = APIEndpoints.BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the status of the backend server
   */
  async getStatus(): Promise<any> {
    try {
      const response = await axios.get(APIEndpoints.STATUS);
      return response.data;
    } catch (error) {
      console.error('Error getting server status:', error);
      throw error;
    }
  }

  /**
   * Set user preferences including job role, description, resume data, and AI configurations
   */
  async setUserPreferences(userPreferences: any): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.USER.PREFERENCES, userPreferences);
      return response.data;
    } catch (error) {
      console.error('Error setting user preferences:', error);
      throw error;
    }
  }

  /**
   * Start a new interview session
   */
  async startSession(userId: string, metadata: any = {}): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.SESSION.CREATE, {
        user_id: userId,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  }

  /**
   * End an interview session
   */
  async endSession(sessionId: string): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.SESSION.END(sessionId));
      return response.data;
    } catch (error) {
      console.error('Error ending session:', error);
      throw error;
    }
  }

  /**
   * Close an interview session
   */
  async closeSession(sessionId: string): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.SESSION.CLOSE(sessionId));
      return response.data;
    } catch (error) {
      console.error('Error closing session:', error);
      throw error;
    }
  }

  /**
   * Get transcriptions for a session
   */
  async getTranscriptions(sessionId: string): Promise<any> {
    try {
      const response = await axios.get(APIEndpoints.SESSION.TRANSCRIPTIONS(sessionId));
      return response.data;
    } catch (error) {
      console.error('Error getting transcriptions:', error);
      throw error;
    }
  }

  /**
   * Get AI responses for a transcription
   */
  async getAIResponse(transcriptionId: string, question: string): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.AI.RESPONSE, {
        transcription_id: transcriptionId,
        question
      });
      return response.data;
    } catch (error) {
      console.error('Error getting AI response:', error);
      throw error;
    }
  }

  /**
   * Submit feedback for an AI response
   */
  async submitFeedback(responseId: string, feedback: 'helpful' | 'not_helpful'): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.AI.FEEDBACK, {
        response_id: responseId,
        feedback
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  /**
   * Get question bank items
   */
  async getQuestionBank(jobTitle?: string, category?: string): Promise<any> {
    try {
      const params: any = {};
      if (jobTitle) params.job_title = jobTitle;
      if (category) params.category = category;
      
      const response = await axios.get(APIEndpoints.QUESTIONS.LIST, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting question bank:', error);
      throw error;
    }
  }

  /**
   * Send a chatbot query to a specific AI provider
   */
  async sendChatbotQuery(
    userId: string,
    question: string,
    provider: 'openai' | 'gemini' | 'both' = 'both'
  ): Promise<any> {
    try {
      const response = await axios.post(APIEndpoints.CHATBOT.QUERY, {
        userId,
        question,
        provider
      });
      return response.data;
    } catch (error) {
      console.error('Error sending chatbot query:', error);
      throw error;
    }
  }

  /**
   * Bind a fresh client session id on the backend (for restart after stop).
   */
  async updateSessionId(userId: string, sessionId: string): Promise<any> {
    try {
      const response = await axios.put(APIEndpoints.USER_SESSION.UPDATE_ID(userId), {
        sessionId,
      });
      console.log(`🆔 Session id updated for ${userId}: ${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error updating session id:', error);
      throw error;
    }
  }

  /**
   * Clear user session data from backend
   * Removes user context and related session data
   */
  async clearUserSession(userId: string): Promise<any> {
    try {
      const response = await axios.delete(APIEndpoints.USER_SESSION.CLEAR(userId));
      console.log(`🧹 User session cleared for: ${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error clearing user session:', error);
      throw error;
    }
  }

  /**
   * Update manual trigger setting for a user
   */
  async updateManualTrigger(userId: string, manualTriggerEnabled: boolean): Promise<any> {
    try {
      const response = await axios.post(`${this.baseUrl}/manual-trigger/update`, {
        userId,
        manualTriggerEnabled
      });
      console.log(`🔘 Manual trigger updated for ${userId}: ${manualTriggerEnabled}`);
      return response.data;
    } catch (error) {
      console.error('Error updating manual trigger:', error);
      throw error;
    }
  }

  /**
   * Get manual trigger status for a user
   */
  async getManualTriggerStatus(userId: string): Promise<any> {
    try {
      const response = await axios.get(`${this.baseUrl}/manual-trigger/status/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting manual trigger status:', error);
      throw error;
    }
  }

  /**
   * Manually trigger AI response generation for a transcription
   */
  async manualGenerateResponse(
    userId: string,
    transcriptionId: string,
    transcriptionText: string,
    provider: 'openai' | 'gemini' | 'both' = 'both'
  ): Promise<any> {
    try {
      const response = await axios.post(`${this.baseUrl}/manual-trigger/generate`, {
        userId,
        transcriptionId,
        transcriptionText,
        provider
      });
      console.log(`🔘 Manual generation triggered for transcription: ${transcriptionId}`);
      return response.data;
    } catch (error) {
      console.error('Error triggering manual generation:', error);
      throw error;
    }
  }
}
